import { CheckCircleIcon, AlertTriangleIcon, ShieldCheckIcon } from '../Icons.js';
import { getBeginnerRiskLabel } from '../../lib/riskExplanation.js';
import { getConfidenceLabel } from '../../data/metric-explanations.js';

export interface ProInvestorSummaryProps {
  /** Risk assessment score on 0-10 scale */
  score: number;
  /** Overall risk level: GREEN | YELLOW | RED | UNKNOWN */
  riskLevel: string;
  /** Analysis confidence 0-100 */
  confidence?: number;
  /** Confidence level label */
  confidenceLevel?: string;
  /** Whether this token is in a verified database */
  verifiedSafe?: boolean;
  /** Source/reason for verified designation */
  verifiedSafeSource?: string;
  /** AI-generated analysis summary */
  summary?: string;
}

const RISK_COLORS: Record<string, string> = {
  GREEN: '#27ae60',
  YELLOW: '#f39c12',
  RED: '#e74c3c',
  UNKNOWN: '#95a5a6',
};

const RISK_VERDICTS: Record<string, string> = {
  GREEN: 'Low Risk Indicators',
  YELLOW: 'Moderate Risk Indicators',
  RED: 'High Risk Indicators',
  UNKNOWN: 'Insufficient Data',
};

const RECOMMENDATIONS: Record<string, { label: string; detail: string }> = {
  GREEN: {
    label: 'Proceed Normally',
    detail: 'Our automated checks did not detect significant risk indicators. Standard due diligence still applies.',
  },
  YELLOW: {
    label: 'Use Caution',
    detail: 'Some risk indicators were detected. Review the specific findings below before making decisions.',
  },
  RED: {
    label: 'High Risk — Review Carefully',
    detail: 'Multiple significant risk indicators were detected. Carefully review all findings before proceeding.',
  },
  UNKNOWN: {
    label: 'Insufficient Data',
    detail: 'Not enough data sources were available to produce a reliable assessment. Exercise extra caution.',
  },
};

const RISK_ICONS: Record<string, typeof ShieldCheckIcon> = {
  GREEN: ShieldCheckIcon,
  YELLOW: AlertTriangleIcon,
  RED: AlertTriangleIcon,
  UNKNOWN: AlertTriangleIcon,
};

export function ProInvestorSummary({
  score,
  riskLevel,
  confidence,
  verifiedSafe,
  verifiedSafeSource,
  summary,
}: ProInvestorSummaryProps) {
  const color = RISK_COLORS[riskLevel] ?? RISK_COLORS.UNKNOWN;
  const verdict = RISK_VERDICTS[riskLevel] ?? RISK_VERDICTS.UNKNOWN;
  const rec = RECOMMENDATIONS[riskLevel] ?? RECOMMENDATIONS.UNKNOWN;
  const explain = getBeginnerRiskLabel(riskLevel);
  const hasConfidence = typeof confidence === 'number';
  const cl = hasConfidence ? getConfidenceLabel(confidence!) : null;
  const RiskIcon = RISK_ICONS[riskLevel] ?? AlertTriangleIcon;

  return (
    <div className="pro-investor-summary">
      {/* Section header */}
      <div className="pro-investor-header">
        <h2 className="pro-investor-title">Risk Assessment Summary</h2>
        <p className="pro-investor-subtitle">Plain-English intelligence for everyday investors</p>
      </div>

      {/* Recommendation banner — most prominent element */}
      <div
        className="pro-investor-recommendation"
        style={{ borderColor: color, backgroundColor: `${color}10` }}
      >
        <RiskIcon
          className="pro-investor-recommendation-icon"
          style={{ color }}
          aria-hidden="true"
        />
        <div className="pro-investor-recommendation-content">
          <span className="pro-investor-recommendation-label" style={{ color }}>
            {rec.label}
          </span>
          <p className="pro-investor-recommendation-detail">{rec.detail}</p>
        </div>
      </div>

      {/* Score + Verdict row */}
      <div className="pro-investor-score-row">
        <div className="pro-investor-score">
          <span className="pro-investor-score-value" style={{ color }}>
            {score.toFixed(1)}
          </span>
          <span className="pro-investor-score-scale">/ 10</span>
        </div>
        <div className="pro-investor-verdict-col">
          <span className="pro-investor-verdict" style={{ color, borderColor: color }}>
            {verdict}
          </span>
          {hasConfidence && cl && (
            <span className="pro-investor-confidence">
              <span className="pro-investor-confidence-label">Confidence:</span>{' '}
              <span className="pro-investor-confidence-value" style={{ color: cl.color }}>
                {Math.round(confidence!)}% ({cl.label})
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Verified Token Badge */}
      {verifiedSafe && (
        <div className="pro-investor-verified">
          <CheckCircleIcon className="pro-investor-verified-icon" aria-hidden="true" />
          <span>Verified Token Contract</span>
          {verifiedSafeSource && (
            <span className="pro-investor-verified-source">Source: {verifiedSafeSource}</span>
          )}
        </div>
      )}

      {/* What This Means + What You Should Do */}
      <div className="pro-investor-explain">
        <div className="pro-investor-explain-block">
          <h4 className="pro-investor-explain-heading">What This Means</h4>
          <p className="pro-investor-explain-text">{explain.shortExplain}</p>
        </div>
        <div className="pro-investor-explain-block">
          <h4 className="pro-investor-explain-heading">What You Should Do</h4>
          <p className="pro-investor-explain-text">{explain.advice}</p>
        </div>
      </div>

      {/* How This Score Works — educational methodology */}
      <details className="pro-investor-methodology">
        <summary className="pro-investor-methodology-toggle">
          How This Score Works
        </summary>
        <div className="pro-investor-methodology-content">
          <p>
            This score aggregates data from multiple independent blockchain analysis
            providers. Each provider evaluates the token contract, liquidity, holder
            distribution, and social signals independently. The final score reflects
            the weighted consensus across all available data sources.
          </p>
          <p>
            <strong>Score range:</strong> 0.0 (highest risk indicators) to 10.0 (lowest risk indicators).
            A higher score means fewer automated risk signals were detected.
          </p>
          <p>
            <strong>Confidence level</strong> reflects how many data sources responded
            and whether they agreed. High confidence means multiple sources confirmed
            the same result.
          </p>
        </div>
      </details>

      {/* AI Summary */}
      {summary && (
        <div className="pro-investor-ai-summary">
          <h4 className="pro-investor-ai-summary-heading">AI Analysis Summary</h4>
          <p>{summary}</p>
        </div>
      )}

      {/* Legal disclaimer */}
      <p className="pro-investor-disclaimer">
        This assessment reflects automated risk indicators, does not eliminate risk, and should not be interpreted as assurance. Not financial advice.
      </p>
    </div>
  );
}
